import { ENDPOINTS } from 'shared/constants/ENDPOINTS';
import { useAxios } from 'shared/hooks/useAxios';
import {
  ILinkProfiles,
  IMultipleProfilePayload,
  INewProfileMatchReponsePayload,
  IProfileCreatePayload,
  IProfileCreateResponsePayload,
  IProfileGraphQL,
  IProfileGraphQLResponse,
  IProfileMatchReponsePayload,
  IProfileMediaResponsePayload,
  IProfileMediaSetSelectPayload,
  IProfilePrimaryImageResponsePayload,
  IProfileResponsePayload,
  IProfilesGraphQL,
  IProfileTabDetailReponsePayload,
  IProfileUpdatePayload,
  ISingleProfileGraphQL,
  ISingleProfileResponsePayload,
} from 'shared/interfaces/IProfile';
import { IMediaFileType } from 'shared/interfaces/utils/IMediaFileType';
import { GET_PROFILE, GET_PROFILES, LINKS_PROFILES } from 'shared/queries/profile.queries';
import { authToken } from 'shared/utils/authToken';
const { GET, POST, DELETE, PATCH } = useAxios();

export const profileDao = () => {
  const { getAuthToken } = authToken();

  const getProfiles = async () => {
    const response = await GET<IProfileResponsePayload>({
      url: `${ENDPOINTS.PROFILE}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    return response.data;
  };

  const getProfilesGraphQL = async (searchString: string) => {
    const response = await POST<IProfilesGraphQL>({
      url: `${ENDPOINTS.GRAPHQL}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: GET_PROFILES,
        variables: { search: searchString },
      },
    });

    return response.data;
  };

  const getProfileLinks = async () => {
    const response = await POST<ILinkProfiles>({
      url: `${ENDPOINTS.GRAPHQL}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      data: {
        query: LINKS_PROFILES,
      },
    });

    return response.data;
  };

  const getSingleProfile = async (id: string) => {
    const response = await GET<ISingleProfileResponsePayload>({
      url: `${ENDPOINTS.PROFILE}/${id}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const getSingleProfileGraphQL = async (id: string) => {
    const response = await POST<IProfileGraphQLResponse>({
      url: ENDPOINTS.GRAPHQL,
      data: {
        query: GET_PROFILE,
        variables: { id },
      },
    });

    return response.data;
  };

  const getProfileTabDetail = async (id: string, tab: string) => {
    const response = await GET<IProfileTabDetailReponsePayload>({
      url: `${ENDPOINTS.PROFILE}/${id}/${tab}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    return response.data;
  };

  const createProfile = async (payload: IProfileCreatePayload) => {
    const response = await POST<IProfileCreateResponsePayload>({
      url: `${ENDPOINTS.PROFILE}`,
      data: payload,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const createMultipleProfiles = async (payload: IMultipleProfilePayload) => {
    const response = await POST<IProfileMatchReponsePayload>({
      url: `${ENDPOINTS.MULTIPLE_PROFILE}`,
      data: payload,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const updateProfile = async (id: string, payload: IProfileUpdatePayload) => {
    const response = await PATCH({
      url: `${ENDPOINTS.PROFILE}/${id}`,
      data: payload,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    return response.data;
  };

  const getProfilePrimaryImage = async (profileId: string) => {
    const response = await GET<IProfilePrimaryImageResponsePayload>({
      url: `${ENDPOINTS.PROFILE}/${profileId}/primary_image`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    return response.data;
  };

  const setProfilePrimaryImage = async (
    profileId: string,
    formData: FormData,
    onProgress?: (current: number) => void,
  ) => {
    const response = await POST<IProfilePrimaryImageResponsePayload>({
      url: `${ENDPOINTS.PROFILE}/${profileId}/primary_image`,
      data: formData,
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          onProgress((progressEvent.loaded / progressEvent.total) * 100);
        }
      },
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const getProfileMedia = async (profileId: string, fileType?: IMediaFileType) => {
    const response = await GET<IProfileMediaResponsePayload>({
      url: `${ENDPOINTS.PROFILE}/${profileId}/medium_attachments`,
      params: fileType,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    return response.data;
  };

  const setSelectProfileMedia = async (profileId: string, payload: IProfileMediaSetSelectPayload) => {
    const response = await POST<IProfileResponsePayload>({
      url: `${ENDPOINTS.PROFILE}/${profileId}/medium_attachments`,
      data: { media: [payload] },
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const unSelectProfileMedia = async (profileId: string, profileMediaId: string) => {
    const response = await DELETE<IProfileResponsePayload>({
      url: `${ENDPOINTS.PROFILE}/${profileId}/medium_attachments/${profileMediaId}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const updateProfileMediaSort = async (profileId: string, sort: { id: string; sort: number }[]) => {
    const response = await PATCH<IProfileResponsePayload>({
      url: `${ENDPOINTS.PROFILE}/${profileId}/medium_attachments/sort`,
      data: {
        sorted_medium_attachments: sort,
      },
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const getProfileMatch = async (ghosted: boolean, first_name: string, last_name: string, project_slug?: string) => {
    if (!first_name) return Promise.reject('First name and last name are required');
    const response = await GET<INewProfileMatchReponsePayload>({
      url: `${ENDPOINTS.PROFILE}/matches`,
      params: {
        ghosted,
        first_name,
        last_name,
        project_slug,
      },
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const claimGhostedProfile = async (
    profile_ids: string[],
    medium_id?: string,
    user_id?: string,
    is_guardian?: boolean,
  ) => {
    const response = await PATCH<IProfileResponsePayload>({
      url: `${ENDPOINTS.CLAIM_MULTIPLE_PROFILES}`,
      data: {
        profile_ids,
        medium_id,
        user_id,
        ...(is_guardian && { is_guardian: true }),
      },
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const archiveProfile = async (profileId: string) => {
    const response = await PATCH<IProfileResponsePayload>({
      url: `${ENDPOINTS.PROFILE}/${profileId}/archived`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    return response.data;
  };

  return {
    archiveProfile,
    createMultipleProfiles,
    getProfiles,
    getProfilesGraphQL,
    getSingleProfile,
    getSingleProfileGraphQL,
    getProfileTabDetail,
    createProfile,
    updateProfile,
    getProfilePrimaryImage,
    setProfilePrimaryImage,
    getProfileMedia,
    setSelectProfileMedia,
    unSelectProfileMedia,
    updateProfileMediaSort,
    getProfileMatch,
    claimGhostedProfile,
    getProfileLinks,
  };
};
